import React from "react";
import { Box, DialogContentText, Tooltip } from "@mui/material";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import HomeIcon from "@mui/icons-material/Home";
import { PieChart } from "@mui/x-charts/PieChart";

interface CommunityBreakdownProps {
     communityConciergeBreakdown: {
          concierges: Array<{ name: string; email: string; count: number }>;
          unassignedCount: number;
          salesforceCount: number;
          salesforceAssignments?: Array<{ name: string; email: string; count: number }>;
          hasMismatch: boolean;
          totalConcierges: number;
          totalAssignedInCommunity?: number;
          isSingleConciergeCurrentUser?: boolean;
          communityConciergeEmail: string | null;
          communityConciergeDisplayName: string | null;
          totalAssignable: number;
          totalIncludingSalesforce?: number;
     } | null;
}

export const CommunityBreakdown: React.FC<CommunityBreakdownProps> = ({
     communityConciergeBreakdown,
}) => {
     if (!communityConciergeBreakdown) return null;

     const colors = [
          "#1976d2", // Blue
          "#2e7d32", // Green
          "#ed6c02", // Orange
          "#9c27b0", // Purple
          "#d32f2f", // Red
          "#0288d1", // Light Blue
          "#388e3c", // Dark Green
          "#f57c00", // Dark Orange
     ];

     return (
          <>
               <DialogContentText
                    style={{
                         fontSize: "14px",
                         color: "#333",
                         fontWeight: "600",
                         marginBottom: "8px"
                    }}
               >
                    Community Assignment Insights
               </DialogContentText>
               {/* Horizontal divider bar */}
               <Box
                    sx={{
                         width: "100%",
                         height: "2px",
                         backgroundColor: "#e0e0e0",
                         marginBottom: "12px",
                         borderRadius: "1px"
                    }}
               />
               <Box sx={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                    <Box sx={{ marginLeft: "16px", marginRight: "16px" }}>
                         {/* Chart on left, vertical divider, concierge details on right */}
                         <Box sx={{ display: "flex", flexDirection: "row", alignItems: "stretch", justifyContent: "flex-start", gap: 0, marginBottom: "8px", flexWrap: "nowrap" }}>
                              {/* Doughnut chart: CLO assignable + Salesforce (display only) */}
                              {(() => {
                                   const totalAll = communityConciergeBreakdown.totalIncludingSalesforce ?? communityConciergeBreakdown.totalAssignable;
                                   const hasSalesforce = (communityConciergeBreakdown.salesforceCount ?? 0) > 0;
                                   const pieData: Array<{ id: string; value: number; label: string; color: string }> = [];
                                   communityConciergeBreakdown.concierges.forEach((concierge, index) => {
                                        if (concierge.count > 0) {
                                             pieData.push({
                                                  id: `concierge-${concierge.email}`,
                                                  value: concierge.count,
                                                  label: concierge.name,
                                                  color: colors[index % colors.length],
                                             });
                                        }
                                   });
                                   if (communityConciergeBreakdown.unassignedCount > 0) {
                                        pieData.push({
                                             id: "unassigned",
                                             value: communityConciergeBreakdown.unassignedCount,
                                             label: "Unassigned",
                                             color: "#9e9e9e",
                                        });
                                   }
                                   if (hasSalesforce && communityConciergeBreakdown.salesforceCount > 0) {
                                        pieData.push({
                                             id: "salesforce",
                                             value: communityConciergeBreakdown.salesforceCount,
                                             label: "Salesforce",
                                             color: "#009EDB",
                                        });
                                   }
                                   if (totalAll === 0 || pieData.length === 0) return null;
                                   return (
                                        <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                             <PieChart
                                                  series={[
                                                       {
                                                            data: pieData,
                                                            innerRadius: 44,
                                                            outerRadius: 72,
                                                            startAngle: 0,
                                                            endAngle: 360,
                                                            paddingAngle: 1,
                                                            cornerRadius: 2,
                                                            highlightScope: { faded: "global", highlighted: "item" },
                                                       },
                                                  ]}
                                                  width={220}
                                                  height={180}
                                                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                                                  slotProps={{
                                                       legend: { hidden: true },
                                                  }}
                                             />
                                        </Box>
                                   );
                              })()}

                              {/* Vertical divider between chart and details */}
                              <Box
                                   sx={{
                                        width: "1px",
                                        minHeight: "140px",
                                        alignSelf: "stretch",
                                        backgroundColor: "#e0e0e0",
                                        marginLeft: "16px",
                                        marginRight: "16px",
                                        flexShrink: 0,
                                   }}
                              />

                              {/* Concierge details on right: all percentages use same total (assignable + Salesforce) */}
                              {(() => {
                              const totalAll = communityConciergeBreakdown.totalIncludingSalesforce ?? communityConciergeBreakdown.totalAssignable;
                              const pctLegend = (n: number) => (totalAll > 0 ? (n / totalAll) * 100 : 0);
                              const communityEmailNorm = (communityConciergeBreakdown.communityConciergeEmail ?? "").trim().toLowerCase();
                              const isInCommunityDataset = (email: string) => !!communityEmailNorm && (email || "").trim().toLowerCase() === communityEmailNorm;
                              return (
                                   <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1, minWidth: 0 }}>
                                        {communityConciergeBreakdown.concierges.map((concierge, index) => {
                                             const percentage = pctLegend(concierge.count);
                                             const color = colors[index % colors.length];
                                             const isCommunityAssigned = isInCommunityDataset(concierge.email);
                                             return (
                                                  <Box key={`${concierge.name}-${concierge.email}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                       <Box
                                                            sx={{
                                                                 width: "12px",
                                                                 height: "12px",
                                                                 flexShrink: 0,
                                                                 marginTop: "2px",
                                                                 backgroundColor: color,
                                                                 borderRadius: "2px"
                                                            }}
                                                       />
                                                       <DialogContentText
                                                            style={{
                                                                 fontSize: "12px",
                                                                 color: "#333",
                                                                 display: "flex",
                                                                 alignItems: "center",
                                                                 flexWrap: "wrap",
                                                                 gap: "2px"
                                                            }}
                                                       >
                                                            <span>{concierge.name}</span>
                                                            <Tooltip title={isCommunityAssigned ? "Community assigned" : "Homesite assigned"} placement="top" arrow>
                                                                 <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1, marginTop: "-2px" }}>
                                                                      {isCommunityAssigned ? <LocationCityIcon sx={{ fontSize: 16, color: "#ed6c02", margin: 0, display: "block" }} /> : <HomeIcon sx={{ fontSize: 16, color: "#9c27b0", margin: 0, display: "block" }} />}
                                                                 </Box>
                                                            </Tooltip>
                                                            <span>: <span style={{ fontWeight: 600 }}>{concierge.count}</span> homesite{concierge.count === 1 ? "" : "s"} (<span style={{ fontWeight: 600 }}>{percentage.toFixed(1)}%</span>)</span>
                                                       </DialogContentText>
                                                  </Box>
                                             );
                                        })}
                                        {/* Show community dataset concierge as separate row when they have 0 homesites and are not in the list */}
                                        {communityEmailNorm !== "" &&
                                             communityConciergeBreakdown.communityConciergeDisplayName &&
                                             !communityConciergeBreakdown.concierges.some((c) => (c.email || "").trim().toLowerCase() === communityEmailNorm) && (
                                                  <Box key="community-concierge-0" sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                       <Box
                                                            sx={{
                                                                 width: "12px",
                                                                 height: "12px",
                                                                 flexShrink: 0,
                                                                 marginTop: "2px",
                                                                 backgroundColor: colors[0],
                                                                 borderRadius: "2px"
                                                            }}
                                                       />
                                                       <DialogContentText style={{ fontSize: "12px", color: "#333", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "2px" }}>
                                                            <span>{communityConciergeBreakdown.communityConciergeDisplayName}</span>
                                                            <Tooltip title="Community assigned" placement="top" arrow>
                                                                 <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1, marginTop: "-2px" }}>
                                                                      <LocationCityIcon sx={{ fontSize: 16, color: "#ed6c02", margin: 0, display: "block" }} />
                                                                 </Box>
                                                            </Tooltip>
                                                            <span>: <span style={{ fontWeight: 600 }}>0</span> homesites (<span style={{ fontWeight: 600 }}>0.0%</span>)</span>
                                                       </DialogContentText>
                                                  </Box>
                                             )}
                                        {communityConciergeBreakdown.unassignedCount > 0 && (
                                             <Box key="unassigned-legend" sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                  <Box
                                                       sx={{
                                                            width: "12px",
                                                            height: "12px",
                                                            flexShrink: 0,
                                                            marginTop: "2px",
                                                            backgroundColor: "#9e9e9e",
                                                            borderRadius: "2px"
                                                       }}
                                                  />
                                                  <DialogContentText
                                                       style={{
                                                            fontSize: "12px",
                                                            color: "#666",
                                                            fontStyle: "italic"
                                                       }}
                                                  >
                                                       Unassigned: <span style={{ fontWeight: 600 }}>{communityConciergeBreakdown.unassignedCount}</span> homesite{communityConciergeBreakdown.unassignedCount === 1 ? "" : "s"} (<span style={{ fontWeight: 600 }}>{pctLegend(communityConciergeBreakdown.unassignedCount).toFixed(1)}%</span>)
                                                  </DialogContentText>
                                             </Box>
                                        )}
                                        {communityConciergeBreakdown.salesforceCount > 0 && (
                                             <Box key="salesforce-legend" sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                       <Box
                                                            sx={{
                                                                 width: "12px",
                                                                 height: "12px",
                                                                 flexShrink: 0,
                                                                 marginTop: "2px",
                                                                 backgroundColor: "#009EDB",
                                                                 borderRadius: "2px"
                                                            }}
                                                       />
                                                       <DialogContentText
                                                            style={{
                                                                 fontSize: "12px",
                                                                 color: "#333"
                                                            }}
                                                       >
                                                            Salesforce: <span style={{ fontWeight: 600 }}>{communityConciergeBreakdown.salesforceCount}</span> homesite{communityConciergeBreakdown.salesforceCount === 1 ? "" : "s"} (<span style={{ fontWeight: 600 }}>{pctLegend(communityConciergeBreakdown.salesforceCount).toFixed(1)}%</span>) (assigned in Salesforce — not available for assign/unassign/reassign here)
                                                       </DialogContentText>
                                                  </Box>
                                                  {(communityConciergeBreakdown.salesforceAssignments?.length ?? 0) > 0 && (
                                                       <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, marginLeft: "20px", paddingLeft: "12px" }}>
                                                            {communityConciergeBreakdown.salesforceAssignments!.map((a, i) => {
                                                                 const sfPercentage = pctLegend(a.count);
                                                                 const subColor = colors[i % colors.length];
                                                                 return (
                                                                      <Box key={`sf-${a.email}-${a.name}-${i}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                                           <Box
                                                                                sx={{
                                                                                     width: "6px",
                                                                                     height: "6px",
                                                                                     flexShrink: 0,
                                                                                     marginTop: "6px",
                                                                                     marginLeft: "3px",
                                                                                     backgroundColor: subColor,
                                                                                     borderRadius: "50%"
                                                                                }}
                                                                           />
                                                                           <DialogContentText
                                                                                style={{
                                                                                     fontSize: "12px",
                                                                                     color: "#333"
                                                                                }}
                                                                           >
                                                                                {a.name}: <span style={{ fontWeight: 600 }}>{a.count}</span> homesite{a.count === 1 ? "" : "s"} (<span style={{ fontWeight: 600 }}>{sfPercentage.toFixed(1)}%</span>)
                                                                           </DialogContentText>
                                                                      </Box>
                                                                 );
                                                            })}
                                                       </Box>
                                                  )}
                                             </Box>
                                        )}
                                   </Box>
                              );
                         })()}
                         </Box>
                    <DialogContentText
                         style={{
                              fontSize: "13px",
                              color: "#666",
                              fontWeight: "500",
                              marginTop: "12px",
                              marginBottom: "0"
                         }}
                    >
                         {communityConciergeBreakdown.hasMismatch && (
                              <span style={{ color: "#d32f2f", fontWeight: "600", marginRight: "8px" }}>
                                   Multiple Concierge:
                              </span>
                         )}
                         No of Concierges assigned in this community:{" "}
                         <span style={{ fontWeight: 600 }}>
                              {communityConciergeBreakdown.totalAssignedInCommunity ?? communityConciergeBreakdown.totalConcierges}
                              {communityConciergeBreakdown.isSingleConciergeCurrentUser ? " (You)" : ""}
                         </span>
                    </DialogContentText>
                    </Box>
               </Box>
               </>
     );
};
